import React, { useCallback, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import Papa from 'papaparse';
import { validate as isValidUUID } from 'uuid';
import dayjs from '~/timeInit';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { toPromoBrand } from '~/consts/promoBrand';

import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';
import { allVoucherUsersQueryKeys } from '~/hooks/apis/promo/useListAllVoucherUsers';

import { CreateVoucherUsersResponse } from '~/services/PromoService';
import { SYDNEY_TIMEZONE } from '~/services/TimeService';

import { normalizeToISODate } from '~/utils/dateFormatter';

const BATCH_SIZE = 500;
const CSV_HEADERS = ['user_id', 'userId', 'customer_id', 'customerId'];

interface ResultsSummary {
  created: number;
  updated: number;
  reactivated: number;
  skipped: Array<{ userId: string; reason: string }>;
}

const EMPTY_RESULTS: ResultsSummary = { created: 0, updated: 0, reactivated: 0, skipped: [] };

function accumulateResults(
  prev: ResultsSummary,
  response: CreateVoucherUsersResponse,
  batchSize: number,
): ResultsSummary {
  const r = response.result as CreateVoucherUsersResponse['result'] & { voucherUsers?: Array<unknown> };
  const hasNewFormat = !!(r?.createdVoucherUsers || r?.updatedVoucherUsers || r?.reactivatedVoucherUsers);
  if (!hasNewFormat) {
    // Legacy response: only has `voucherUsers` array (or nothing)
    return { ...prev, created: prev.created + (r?.voucherUsers?.length ?? batchSize) };
  }
  return {
    created: prev.created + (r?.createdVoucherUsers?.length ?? 0),
    updated: prev.updated + (r?.updatedVoucherUsers?.length ?? 0),
    reactivated: prev.reactivated + (r?.reactivatedVoucherUsers?.length ?? 0),
    skipped: prev.skipped.concat((r?.skippedVoucherUsers ?? []).map((s) => ({ userId: s.userId, reason: s.reason }))),
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  voucherId: string;
  brand: string;
}

export default function BulkUploadVoucherUsersDialog({ open, onClose, voucherId, brand }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const createVoucherUsersMutation = useCreateVoucherUsers();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cancelledRef = useRef(false);
  const parseSessionRef = useRef(0);
  const promoBrand = toPromoBrand(brand);

  const [expiredAt, setExpiredAt] = useState(() => dayjs().tz(SYDNEY_TIMEZONE).add(1, 'year').format('YYYY-MM-DD'));
  const [allowVoucherReuse, setAllowVoucherReuse] = useState(false);
  const [reactivateExpiredVouchers, setReactivateExpiredVouchers] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedUserIds, setParsedUserIds] = useState<Array<string>>([]);
  const [invalidIds, setInvalidIds] = useState<Array<string>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [results, setResults] = useState<ResultsSummary>(EMPTY_RESULTS);

  const resetState = useCallback(() => {
    cancelledRef.current = false;
    parseSessionRef.current += 1;
    setExpiredAt(dayjs().tz(SYDNEY_TIMEZONE).add(1, 'year').format('YYYY-MM-DD'));
    setAllowVoucherReuse(false);
    setReactivateExpiredVouchers(false);
    setSelectedFile(null);
    setParsedUserIds([]);
    setInvalidIds([]);
    setIsUploading(false);
    setProgress(0);
    setStatusMessage(null);
    setErrorMessage(null);
    setResults(EMPTY_RESULTS);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleClose = useCallback(() => {
    if (isUploading) {
      cancelledRef.current = true;
      setIsUploading(false);
    }
    resetState();
    onClose();
  }, [isUploading, resetState, onClose]);

  const parseUserIds = useCallback((ids: Array<string>) => {
    const trimmed = ids.map((id) => id.trim()).filter(Boolean);
    const deduped = Array.from(new Set(trimmed));
    const valid = deduped.filter((id) => isValidUUID(id));
    const invalid = deduped.filter((id) => !isValidUUID(id));
    return { valid, invalid };
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      setSelectedFile(file);
      setErrorMessage(null);
      setStatusMessage(null);
      setParsedUserIds([]);
      setInvalidIds([]);

      const parseWithHeader = () =>
        new Promise<Papa.ParseResult<Record<string, string>>>((resolve) => {
          Papa.parse<Record<string, string>>(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => resolve(results),
          });
        });

      const parseWithoutHeader = () =>
        new Promise<Papa.ParseResult<Array<string>>>((resolve) => {
          Papa.parse<Array<string>>(file, {
            header: false,
            skipEmptyLines: true,
            complete: (results) => resolve(results),
          });
        });

      const currentSession = parseSessionRef.current;

      (async () => {
        const headerResults = await parseWithHeader();
        const fields = headerResults.meta.fields || [];
        const headerField = fields.find((field) => CSV_HEADERS.includes(field));

        let userIds: Array<string>;
        if (headerField) {
          userIds = headerResults.data
            .map((row) => row?.[headerField] ?? '')
            .map((value) => String(value).trim())
            .filter(Boolean);
        } else {
          const noHeaderResults = await parseWithoutHeader();
          const rawIds = noHeaderResults.data
            .map((row) => (Array.isArray(row) ? row[0] : ''))
            .map((value) => String(value).trim())
            .filter(Boolean);

          if (rawIds.length > 0 && !isValidUUID(rawIds[0])) {
            userIds = rawIds.slice(1);
          } else {
            userIds = rawIds;
          }
        }

        if (parseSessionRef.current !== currentSession) {
          return;
        }

        const { valid, invalid } = parseUserIds(userIds);
        setParsedUserIds(valid);
        setInvalidIds(invalid);
        if (invalid.length > 0) {
          enqueueSnackbar(`${invalid.length} invalid user IDs were ignored`, { variant: 'warning' });
        }
      })().catch((error) => {
        setErrorMessage(`Failed to parse CSV: ${error.message || 'Unknown error'}`);
      });
    },
    [enqueueSnackbar, parseUserIds],
  );

  const handleSubmit = useCallback(async () => {
    if (!expiredAt) {
      setErrorMessage('Expires At date is required');
      return;
    }
    if (parsedUserIds.length === 0) {
      setErrorMessage('Upload a CSV file with at least one valid user ID');
      return;
    }
    if (!promoBrand) {
      setErrorMessage('A valid promo brand is required');
      return;
    }

    cancelledRef.current = false;
    setIsUploading(true);
    setErrorMessage(null);
    setStatusMessage(null);
    setProgress(0);
    setResults(EMPTY_RESULTS);

    const batches: Array<Array<string>> = [];
    for (let i = 0; i < parsedUserIds.length; i += BATCH_SIZE) {
      batches.push(parsedUserIds.slice(i, i + BATCH_SIZE));
    }

    let totals: ResultsSummary = { ...EMPTY_RESULTS, skipped: [] };

    for (let index = 0; index < batches.length; index += 1) {
      if (cancelledRef.current) {
        setResults(totals);
        setStatusMessage(`Cancelled after processing ${index}/${batches.length} batches`);
        setIsUploading(false);
        return;
      }
      const batch = batches[index];
      try {
        const response = await createVoucherUsersMutation.mutateAsync({
          voucher_id: voucherId,
          expired_at: normalizeToISODate(expiredAt),
          brand: promoBrand,
          allow_voucher_reuse: allowVoucherReuse,
          reactivate_expired_vouchers: reactivateExpiredVouchers,
          voucher_users: batch.map((userId) => ({ user_id: userId })),
        });
        totals = accumulateResults(totals, response, batch.length);
        setResults(totals);
        setProgress(Math.round(((index + 1) / batches.length) * 100));
        const processed = totals.created + totals.updated + totals.reactivated + totals.skipped.length;
        setStatusMessage(`Processed batch ${index + 1}/${batches.length} — ${processed} users processed`);
      } catch (error) {
        setResults(totals);
        setErrorMessage(`Batch ${index + 1} failed: ${(error as Error).message}`);
        setIsUploading(false);
        return;
      }
    }

    setIsUploading(false);
    const totalAdded = totals.created + totals.updated + totals.reactivated;
    setStatusMessage(`Done — ${totalAdded} users added to voucher`);
    enqueueSnackbar(`Processed ${parsedUserIds.length} voucher users`, { variant: 'success' });
    queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] });
    queryClient.invalidateQueries({ queryKey: allVoucherUsersQueryKeys.all });
  }, [
    parsedUserIds,
    createVoucherUsersMutation,
    voucherId,
    expiredAt,
    allowVoucherReuse,
    reactivateExpiredVouchers,
    promoBrand,
    enqueueSnackbar,
    queryClient,
  ]);

  const hasResults =
    results.created > 0 || results.updated > 0 || results.reactivated > 0 || results.skipped.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Upload Voucher Users</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Expires At"
            type="date"
            value={expiredAt}
            onChange={(event) => setExpiredAt(event.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={isUploading}
            required
            fullWidth
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={allowVoucherReuse}
                onChange={(e) => setAllowVoucherReuse(e.target.checked)}
                disabled={isUploading}
              />
            }
            label="Allow voucher reuse (users who already redeemed will receive a new assignment)"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={reactivateExpiredVouchers}
                onChange={(e) => setReactivateExpiredVouchers(e.target.checked)}
                disabled={isUploading}
              />
            }
            label="Reactivate expired vouchers (expired unused assignments will have their expiry bumped)"
          />

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>
              Expected CSV format:
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'grey.800' : 'background.paper'),
                borderRadius: 1,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }}
            >
              <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>
                user_id
              </Box>
              {'\n'}
              <Box component="span" sx={{ color: 'text.primary' }}>
                a1b2c3d4-e5f6-7890-abcd-ef1234567890
              </Box>
              {'\n'}
              <Box component="span" sx={{ color: 'text.primary' }}>
                b2c3d4e5-f6a7-8901-bcde-f12345678901
              </Box>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
              First row should be the column header: <strong>user_id</strong>
              <br />
              Each subsequent row should contain one user UUID
            </Typography>
          </Paper>

          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              component="label"
              variant="contained"
              tabIndex={-1}
              disabled={!!selectedFile || isUploading}
              startIcon={selectedFile ? <CheckBoxIcon /> : <CloudUploadIcon />}
            >
              {selectedFile ? 'File Selected' : 'Choose CSV File'}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv, text/csv"
                onChange={handleFileChange}
                style={{
                  clip: 'rect(0 0 0 0)',
                  clipPath: 'inset(50%)',
                  height: 1,
                  overflow: 'hidden',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  whiteSpace: 'nowrap',
                  width: 1,
                }}
              />
            </Button>
            {selectedFile && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {parsedUserIds.length.toLocaleString()} valid user IDs
                </Typography>
              </Box>
            )}
          </Stack>

          {invalidIds.length > 0 && (
            <Alert severity="warning">{invalidIds.length} invalid user IDs were ignored (not valid UUIDs).</Alert>
          )}

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {statusMessage && <Alert severity={isUploading ? 'info' : 'success'}>{statusMessage}</Alert>}
          {isUploading && <LinearProgress variant="determinate" value={progress} />}

          {hasResults && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Results Summary
              </Typography>
              <Stack spacing={0.5}>
                {results.created > 0 && (
                  <Typography variant="body2">
                    Created: <strong>{results.created}</strong>
                  </Typography>
                )}
                {results.updated > 0 && (
                  <Typography variant="body2">
                    Updated (reused): <strong>{results.updated}</strong>
                  </Typography>
                )}
                {results.reactivated > 0 && (
                  <Typography variant="body2">
                    Reactivated: <strong>{results.reactivated}</strong>
                  </Typography>
                )}
                {results.skipped.length > 0 && (
                  <Box>
                    <Typography variant="body2" color="warning.main">
                      Skipped: <strong>{results.skipped.length}</strong>
                    </Typography>
                    <Box
                      component="ul"
                      sx={{ m: 0, mt: 0.5, pl: 2, maxHeight: 160, overflow: 'auto', fontSize: '0.8125rem' }}
                    >
                      {results.skipped.map((s) => (
                        <li key={s.userId}>
                          <Typography variant="caption" component="span" sx={{ fontFamily: 'monospace' }}>
                            {s.userId}
                          </Typography>
                          {' — '}
                          {s.reason === 'already_redeemed' ? 'already redeemed' : 'expired unused'}
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose}>
          {isUploading ? 'Cancel Upload' : statusMessage && !errorMessage ? 'Close' : 'Cancel'}
        </Button>
        {!statusMessage || errorMessage ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isUploading || parsedUserIds.length === 0 || !expiredAt}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
